import { useState, useCallback, useEffect, Key, useRef } from "react";
import {
    Table,
    Row,
    Col,
    Input,
    Button,
    Tag,
    Popover,
    Tooltip,
    Popconfirm,
} from "antd";
import { showToast } from "@/ts/modals";
import {
    IClientListItem,
    ParentModule,
    ClientTableColumns,
    IClientGroup,
    IClientSubgroup,
    ChannelDetail,
} from "@/ts/common-types";
import { deleteClient, getClientsList } from "@/ts/retrieve-data";
import * as Sentry from "@sentry/react";
import { debounce } from "underscore";
import { GrEdit } from "react-icons/gr";
import { ClientForm } from "./components/ClientForm";
import { useNavigate } from "react-router-dom";
import { ColumnsType } from "antd/es/table";
import { usePermissionCheck } from "@/hooks/AllCustomHooks";
import { AiOutlineEye, AiOutlineDelete } from "react-icons/ai";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "@/ts/constants";
import { titleCase, startCase } from "@/ts/utils";
import "@/scss/clients.scss";

const ClientsComponent = () => {
    const navigate = useNavigate();
    const [paginationData, setPaginationData] = useState({
        clientsList: new Map<number, IClientListItem[]>(),
        currentPage: 1,
        pageSize: 10,
        totalClients: 0,
        loading: false,
    });

    const [searchQuery, setSearchQuery] = useState("");
    const [editClientData, setEditClientData] =
        useState<IClientListItem | null>(null);
    const [addClientDisplay, setAddClientDisplay] = useState(false);
    const { canCreateClient, canUpdateClient, canDeleteClient } =
        usePermissionCheck();
    const [metadata, setMetadata] = useState<{
        groups: IClientGroup[];
        subgroups: IClientSubgroup[];
    }>({ groups: [], subgroups: [] });

    // request tracking ref
    const currentRequestId = useRef<number>(0);

    const fetchClients = useCallback(
        async (page: number, pageSize: number) => {
            const thisRequestId = ++currentRequestId.current;

            setPaginationData((prev) => ({
                ...prev,
                loading: true,
            }));

            try {
                const response = await getClientsList({
                    page,
                    pageSize,
                    searchQuery,
                });

                if (thisRequestId !== currentRequestId.current) {
                    return;
                }

                if (response.metadata) {
                    setMetadata(response.metadata);
                }

                setPaginationData((prev) => ({
                    ...prev,
                    clientsList: new Map(prev.clientsList).set(
                        page,
                        response.rows
                    ),
                    currentPage: page,
                    totalClients: response.total,
                    loading: false,
                }));
            } catch (error) {
                if (thisRequestId === currentRequestId.current) {
                    console.error("Error fetching clients:", error);
                    setPaginationData((prev) => ({
                        ...prev,
                        loading: false,
                    }));
                    showToast("danger", "Failed to fetch clients");
                    Sentry.captureException(error);
                }
            }
        },
        [searchQuery]
    );

    const handleSearch = (value: string) => {
        setSearchQuery(value);
        setPaginationData((prev) => ({
            ...prev,
            currentPage: 1,
            clientsList: new Map(),
        }));
        fetchClients(1, paginationData.pageSize);
    };

    const handleEdit = (record: IClientListItem) => {
        setEditClientData(record);
        setAddClientDisplay(true);
    };

    const handleView = (record: IClientListItem) => {
        navigate(
            `/${ParentModule.ADMIN_SETTINGS}/settings/clients/${record.id}`
        );
    };

    const handleDelete = async (
        record: IClientListItem,
        e?: React.MouseEvent
    ) => {
        e?.stopPropagation();
        try {
            await deleteClient(record.id);
            showToast("success", "Client deleted successfully");
            fetchClients(paginationData.currentPage, paginationData.pageSize);
        } catch (error: any) {
            showToast("danger", error.message || "Failed to delete client");
        }
    };

    const debouncedSearch = debounce(handleSearch, 500);

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    useEffect(() => {
        fetchClients(paginationData.currentPage, paginationData.pageSize);
    }, [searchQuery, fetchClients, paginationData.pageSize]);

    const columns: ColumnsType<IClientListItem> = [
        {
            title: ClientTableColumns.ID,
            dataIndex: "id",
            key: "id",
            width: 80,
            sorter: (a: IClientListItem, b: IClientListItem) => a.id - b.id,
        },
        {
            title: ClientTableColumns.Name,
            dataIndex: "name",
            key: "name",
            sorter: (a: IClientListItem, b: IClientListItem) =>
                a.name.localeCompare(b.name),
        },
        {
            title: ClientTableColumns.ClientGroup,
            dataIndex: ["client_group", "name"],
            key: "client_group",
            sorter: (a: IClientListItem, b: IClientListItem) =>
                a.client_group.name.localeCompare(b.client_group.name),
            filters: Array.from(
                new Set(
                    paginationData.clientsList
                        .get(paginationData.currentPage)
                        ?.map((client) => client.client_group.name) || []
                )
            ).map((name) => ({ text: name, value: name })),
            onFilter: (value: Key | boolean, record: IClientListItem) =>
                record.client_group.name === String(value),
        },
        {
            title: ClientTableColumns.ClientSubGroup,
            dataIndex: ["client_subgroup", "name"],
            key: "client_subgroup",
            sorter: (a: IClientListItem, b: IClientListItem) =>
                a.client_subgroup.name.localeCompare(b.client_subgroup.name),
            filters: Array.from(
                new Set(
                    paginationData.clientsList
                        .get(paginationData.currentPage)
                        ?.map((client) => client.client_subgroup.name) || []
                )
            ).map((name) => ({ text: name, value: name })),
            onFilter: (value: Key | boolean, record: IClientListItem) =>
                record.client_subgroup.name === String(value),
        },
        {
            title: ClientTableColumns.Status,
            dataIndex: "status",
            key: "status",
            filters: [
                { text: "Active", value: true },
                { text: "Inactive", value: false },
                { text: "Archived", value: "archived" },
            ],
            onFilter: (value: Key | boolean, record: IClientListItem) =>
                value === "archived"
                    ? record.is_archived
                    : !record.is_archived && record.status === value,
            render: (status: boolean, record: IClientListItem) => (
                <Tag
                    color={
                        record.is_archived ? "gray" : status ? "green" : "red"
                    }
                >
                    <strong>
                        {record.is_archived
                            ? "Archived"
                            : status
                            ? "Active"
                            : "Inactive"}
                    </strong>
                </Tag>
            ),
        },
        {
            title: ClientTableColumns.Subscription,
            key: "subscription",
            sorter: (a: IClientListItem, b: IClientListItem) =>
                a.subscription.plan_name.localeCompare(
                    b.subscription.plan_name
                ),
            filters: Array.from(
                new Set(
                    paginationData.clientsList
                        .get(paginationData.currentPage)
                        ?.map((client) => client.subscription.plan_name) || []
                )
            ).map((name) => ({ text: name, value: name })),
            onFilter: (value: Key | boolean, record: IClientListItem) =>
                record.subscription.plan_name === String(value),
            render: (text: string, record: IClientListItem) => (
                <Popover
                    placement="right"
                    title={
                        <div className="fw-bold py-2 px-3 border-bottom">
                            Subscription Details
                        </div>
                    }
                    content={
                        <div className="py-2 px-3">
                            <div className="d-flex justify-content-between align-items-center py-1">
                                <span className="text-muted me-3">Plan:</span>
                                <span className="fw-semibold text-end">
                                    {startCase(record.subscription.plan_name)}
                                </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-1">
                                <span className="text-muted me-3">Tier:</span>
                                <span className="fw-semibold text-end">
                                    {startCase(record.subscription.tier)}
                                </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-1">
                                <span className="text-muted me-3">Status:</span>
                                <Tag
                                    color={
                                        record.subscription.status === "active"
                                            ? "green"
                                            : "orange"
                                    }
                                    className="m-0"
                                >
                                    {titleCase(record.subscription.status)}
                                </Tag>
                            </div>
                        </div>
                    }
                    styles={{
                        root: {
                            minWidth: "250px",
                            boxShadow: "0 3px 6px rgba(0,0,0,0.16)",
                        },
                        body: { padding: 0 },
                    }}
                    arrow={{ pointAtCenter: true }}
                >
                    <Tag color="blue">
                        {startCase(record.subscription.plan_name)}
                    </Tag>
                </Popover>
            ),
        },
        {
            title: ClientTableColumns.ChannelList,
            dataIndex: "channel_details",
            key: "channel_details",
            sorter: (a: IClientListItem, b: IClientListItem) =>
                (a.channel_details?.length || 0) -
                (b.channel_details?.length || 0),
            filters: Array.from(
                new Set(
                    paginationData.clientsList
                        .get(paginationData.currentPage)
                        ?.flatMap(
                            (client) =>
                                client.channel_details?.map((c) => c.name) || []
                        ) || []
                )
            ).map((name) => ({ text: name, value: name })),
            onFilter: (value: Key | boolean, record: IClientListItem) =>
                record.channel_details?.some((c) => c.name === String(value)) ||
                false,
            render: (channel_details: ChannelDetail[] = []) => (
                <Popover
                    placement="right"
                    title={
                        <div className="fw-bold py-2 px-3 border-bottom">
                            Channels
                        </div>
                    }
                    content={
                        <div className="py-2 px-1">
                            {channel_details.length === 0 ? (
                                <div className="text-muted text-center py-2 px-3">
                                    No channels configured
                                </div>
                            ) : (
                                <div className="px-2">
                                    {channel_details.map((channel, index) => (
                                        <div
                                            key={index}
                                            className="d-flex align-items-center py-1 px-1 mb-1"
                                        >
                                            {channel.logo_url && (
                                                <div className="me-2 d-flex align-items-center justify-content-center channel-logo-container">
                                                    <img
                                                        src={channel.logo_url}
                                                        alt={titleCase(
                                                            channel.name
                                                        )}
                                                        className="img-fluid channel-logo"
                                                    />
                                                </div>
                                            )}
                                            <span className="fw-medium">
                                                {titleCase(channel.name)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    }
                    styles={{
                        root: {
                            minWidth: "210px",
                            boxShadow: "0 3px 6px rgba(0,0,0,0.16)",
                        },
                        body: { padding: 0 },
                    }}
                    arrow={{ pointAtCenter: true }}
                >
                    <span>{channel_details?.length || 0} channels</span>
                </Popover>
            ),
        },
        {
            title: ClientTableColumns.ContactName,
            dataIndex: ["contact", "name"],
            key: "contact_name",
            render: (name: string | null) => name || "-",
            sorter: (a: IClientListItem, b: IClientListItem) => {
                const nameA = a.contact?.name || "";
                const nameB = b.contact?.name || "";
                return nameA.localeCompare(nameB);
            },
        },
        {
            title: ClientTableColumns.ActionButtons,
            key: "actions",
            width: 120,
            render: (text: string, record: IClientListItem) => (
                <div className="d-flex align-items-center gap-2">
                    {canUpdateClient && (
                        <Tooltip title="Edit Client">
                            <Button
                                type="text"
                                className="p-0 btn-sm cursor-pointer"
                                icon={<GrEdit className="fs-5" />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleEdit(record);
                                }}
                            />
                        </Tooltip>
                    )}
                    <Tooltip title="View Client">
                        <Button
                            type="text"
                            className="p-0 btn-sm cursor-pointer"
                            icon={<AiOutlineEye className="fs-5" />}
                            onClick={() => handleView(record)}
                        />
                    </Tooltip>
                    {canDeleteClient && (
                        <Popconfirm
                            title="Delete Client"
                            description="Are you sure you want to delete this client?"
                            onConfirm={(e) => handleDelete(record, e)}
                            onCancel={(e) => e?.stopPropagation()}
                            okText="Yes"
                            cancelText="No"
                            okButtonProps={{
                                danger: true,
                                className: "ms-2",
                            }}
                            overlayClassName="p-3"
                            styles={{ body: { padding: "12px" } }}
                            disabled={record.is_archived}
                        >
                            <Button
                                type="text"
                                danger
                                className="p-0 btn-sm cursor-pointer"
                                icon={<AiOutlineDelete className="fs-5" />}
                                onClick={(e) => e.stopPropagation()}
                                disabled={record.is_archived}
                            />
                        </Popconfirm>
                    )}
                </div>
            ),
        },
    ];

    return (
        <div>
            {!addClientDisplay ? (
                <>
                    <Row className="">
                        <Col span={12}>
                            <h5>
                                All Clients{" "}
                                <span className="text-muted">
                                    ({paginationData.totalClients || 0})
                                </span>
                            </h5>
                        </Col>
                        <Col span={12} className="text-end">
                            <Input
                                placeholder="Search by name"
                                className="border client-search-input"
                                defaultValue={searchQuery}
                                onChange={(e) => {
                                    const value = e.target.value.trim();
                                    debouncedSearch(value);
                                }}
                                allowClear
                            />
                            {canCreateClient && (
                                <Button
                                    type="primary"
                                    className="ms-3"
                                    onClick={() => {
                                        setAddClientDisplay(true);
                                    }}
                                >
                                    Add Client
                                </Button>
                            )}
                        </Col>
                    </Row>

                    <div className="mt-4">
                        <Table
                            columns={columns}
                            dataSource={
                                paginationData.clientsList.get(
                                    paginationData.currentPage
                                ) || []
                            }
                            rowKey="id"
                            onRow={(record) => ({
                                onClick: () => handleView(record),
                                className: record.is_archived
                                    ? "archived-row"
                                    : "active-row",
                            })}
                            pagination={{
                                current: paginationData.currentPage,
                                pageSize: paginationData.pageSize,
                                total: paginationData.totalClients,
                                onChange: (page, pageSize) => {
                                    fetchClients(page, pageSize);
                                },
                                onShowSizeChange: (current, size) => {
                                    setPaginationData((prev) => ({
                                        clientsList: new Map(),
                                        currentPage: 1,
                                        pageSize: size,
                                        totalClients: prev.totalClients,
                                        loading: true,
                                    }));
                                    fetchClients(1, size);
                                },
                                showSizeChanger: true,
                                pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
                            }}
                            loading={paginationData.loading}
                            className="clients-table"
                            scroll={{ y: "calc(100vh - 350px)" }}
                        />
                    </div>
                </>
            ) : (
                <ClientForm
                    editData={editClientData}
                    metadata={metadata}
                    onSuccess={() => {
                        setAddClientDisplay(false);
                        setEditClientData(null);
                        setPaginationData((prev) => ({
                            ...prev,
                            clientsList: new Map(),
                            currentPage: 1,
                            loading: true,
                        }));
                    }}
                    onCancel={() => {
                        setAddClientDisplay(false);
                        setEditClientData(null);
                    }}
                />
            )}
        </div>
    );
};

export default ClientsComponent;
